import React from 'react'
import { usePageViewTracking } from '../core'
import PropTypes from 'prop-types'
import SiteHeader from '../components/SiteHeader'
import ArticleHeader from '../components/ArticleHeader'
import Container from '../components/Container'
import StoreHighlights from '../components/StoreHighlights'
import InStoreServices from '../components/InStoreServices'
import InfoBannerSide from '../components/InfoBannerSide'
import MarketingOne from '../components/MarketingOne'
import MarketingTwo from '../components/MarketingTwo'
// import MarketingTop from '../components/MarketingTop'
import OverrideBanner from '../components/OverrideBanner'
import HelpDetails from '../components/HelpDetails'
import Icon from '../components/Icon'
import NearbyStores from '../components/NearbyStores'
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel,
} from 'react-accessible-accordion'
import {
  getWeekDates,
  formatOpeningTimes,
  getTodaysDate,
  formatAddress,
} from '../core'
import { SEO } from '../components/SEO'

import parkingList from '../data/parking-list.json'

const hiddenStores = [181, 527, 479, 493, 520] //Hide these stores from the details page

const renderTimes = (day, supermarketSpecialTimes) => {
  const currentWeek = getWeekDates({ addWeek: false })
  const date = currentWeek.find(d => d.day_id === day.day_id)?.date
  const specialDate = supermarketSpecialTimes?.specialOpeningTimes.find(
    t => t.date === date
  )

  if (specialDate) {
    if (specialDate?.closed) {
      return (
        <>
          <span className="leading-tight md:w-1/2 xl:w-auto max-w-1/2">
            {specialDate.label}
          </span>{' '}
          <span className="leading-tight text-right whitespace-nowrap md:w-1/2 xl:w-auto ">
            Closed
          </span>
        </>
      )
    } else {
      return (
        <>
          <span className="mb-1 leading-tight md:w-1/2 xl:w-auto max-w-1/2">
            {specialDate.label}
          </span>{' '}
          <span className="pl-1 mb-1 leading-tight text-right md:w-1/2 xl:w-auto whitespace-nowrap">
            {formatOpeningTimes({
              open: specialDate.open,
              close: specialDate.close,
            })}
          </span>
        </>
      )
    }
  } else if (day?.open === '00:00:00' && day?.close === '00:00:00') {
    return (
      <>
        <span className="leading-tight md:w-1/2 xl:w-auto max-w-1/2">
          {day?.day}
        </span>{' '}
        <span className="leading-tight text-right whitespace-nowrap md:w-1/2 xl:w-auto ">
          Closed
        </span>
      </>
    )
  } else {
    return (
      <>
        <span className="mb-1 leading-tight md:w-1/2 xl:w-auto max-w-1/2">
          {day.day}{' '}
        </span>
        <span className="pl-1 mb-1 leading-tight text-right md:w-1/2 xl:w-auto whitespace-nowrap">
          {formatOpeningTimes({
            open: day.open,
            close: day.close,
          })}
        </span>
      </>
    )
  }
}

const renderSpecialOpeningTimes = supermarketSpecialTimes => {
  const nextWeek = getWeekDates({ addWeek: true })

  return nextWeek.map((d, index) => {
    const specialDate = supermarketSpecialTimes?.specialOpeningTimes.find(
      t => t.date === d.date
    )

    if (specialDate) {
      if (specialDate?.closed) {
        return (
          <p
            key={specialDate?.date}
            className={`mb-1 text-error leading-tight font-semibold ${
              index === 0 ? 'border-t  border-body-lighter mt-2 pt-2' : ''
            } flex justify-between px-4 md:px-2 xl:px-4 py-1`}
          >
            <>
              <span className="leading-tight md:w-1/2 xl:w-auto">
                {specialDate.label}
              </span>{' '}
              <span className="pl-1 leading-tight text-right whitespace-nowrap md:w-1/2 xl:w-auto">
                Closed
              </span>
            </>
          </p>
        )
      } else {
        return (
          <p
            key={specialDate?.date}
            className={`mb-1 text-error leading-tight font-semibold ${
              index === 0 ? 'border-t border-body-lighter mt-2 pt-2' : ''
            } flex justify-between  px-4 md:px-2 xl:px-4 py-1`}
          >
            <>
              <span className="leading-tight md:w-1/2 xl:w-auto">
                {specialDate.label}
              </span>{' '}
              <span className="pl-1 leading-tight text-right whitespace-nowrap md:w-1/2 xl:w-auto">
                {formatOpeningTimes({
                  open: specialDate.open,
                  close: specialDate.close,
                })}
              </span>
            </>
          </p>
        )
      }
    } else {
      return null
    }
  })
}

const propTypes = {
  location: PropTypes.object,
}

const StorePage = ({ pageContext }) => {
  usePageViewTracking()

  let dataLayer

  if (typeof window !== `undefined`) {
    dataLayer = window.dataLayer
  }

  function clickCheckIfBusy() {
    if (dataLayer && dataLayer.push) {
      dataLayer.push({
        event: 'popularHours',
      })
    }
  }

  function clickGetDirections() {
    if (dataLayer && dataLayer.push) {
      dataLayer.push({
        event: 'getDirections',
      })
    }
  }

  const {
    pharmacy,
    storeName,
    name,
    region,
    category,
    openingTimes,
    specialOpeningTimes,
    linkedLocations,
    services,
    departments,
    serviceHighlights,
    address,
    location,
    liveLink,
    telephone,
    storeType,
    nearbyStores,
  } = pageContext

  const typeOfStore = storeType === 'Morrisons' ? storeType : 'MorrisonsDaily'
  const isGasStation = storeType === 'Gas Station'

  const supermarketSpecialTimes = specialOpeningTimes.find(
    times => times.name === 'supermarket'
  )
  const pfsOpeningTimes = linkedLocations.find(
    location => location.storeFormat === 'pfs'
  )
  const pfsSpecialTimes = specialOpeningTimes.find(
    times => times.name === 'pfs'
  )

  const dateToday = getTodaysDate(supermarketSpecialTimes, openingTimes)

  const [dayId, setDayId] = React.useState(null)
  const date = new Date()
  const id = date.getDay()
  React.useEffect(() => {
    setDayId(id)
  }, [id])

  //adjust the time of parking allowed
  function formatTime(minutes) {
    if (minutes < 60) {
      return `${minutes} Minute${minutes > 1 ? 's' : ''}`
    } else {
      const hours = minutes / 60
      return `${hours.toFixed(1)} Hour${hours !== 1 ? 's' : ''}`
    }
  }

  //adjust the time of parking allowed
  const parkingItem = parkingList.find(item => item.name === name)

  const hoursOverride = [] //Override these stores hours with a different message

  // const gardenCentres = [
  //   36,
  //   55,
  //   59,
  //   75,
  //   106,
  //   122,
  //   131,
  //   238,
  //   270,
  //   325,
  //   332,
  //   333,
  //   353,
  //   387,
  //   416,
  //   468,
  //   644,
  // ]
  //Below code checks department hours and hides the department if it is closed
  const hasValidOpeningTimes = openingTimes => {
    return Object.values(openingTimes).some(
      time => time.open !== '00:00:00' || time.close !== '00:00:00'
    )
  }

  return (
    <>
      <SiteHeader search sticky storeType={typeOfStore} />
      {/* <MarketingTop /> */}
      <ArticleHeader
        currentCategory={`${storeName}`}
        currentPage={`${storeName}`}
        currentID={name}
        dateToday={dateToday}
        region={`${region}`}
        category={`${category}`}
      />
      <Container>
        <main className="px-0 pt-4 pb-10 lg:pt-6">
          {hiddenStores.includes(name) ? (
            <OverrideBanner
              currentID={name}
              address1={address?.addressLine1}
              address2={address?.addressLine2}
              address3={address?.city}
              address4={address?.postcode}
            />
          ) : (
            <section className="flex flex-col w-full mb-10 md:flex-row">
              <section className="w-full md:w-2/5 lg:w-1/4">
                <div className="mb-2">
                  <h3 className="text-xl font-semibold">Opening Hours</h3>
                </div>
                <Accordion
                  allowZeroExpanded
                  preExpanded={['a']}
                  className="w-full mb-4"
                >
                  <AccordionItem uuid="a">
                    <AccordionItemHeading className="border-0 border-b-2 border-white bg-primary-lighter">
                      <AccordionItemButton className="accordion__button | relative p-4 pr-8 text-lg font-medium outline-none flex justify-between items-center">
                        {isGasStation ? 'Petrol Station' : 'Store'}
                      </AccordionItemButton>
                    </AccordionItemHeading>
                    <AccordionItemPanel className="py-2 text-base">
                      {hoursOverride.includes(name) ? (
                        <span className="block w-full text-lg font-bold text-center text-secondary">
                          {/* New Store Opening 12/12/2024 */}
                        </span>
                      ) : (
                        <>
                          {openingTimes?.map(day => {
                            return (
                              <p
                                key={day.day_id}
                                className={`${
                                  dayId === day.day_id ? 'font-bold' : ''
                                }  flex justify-between px-4  md:px-2 xl:px-4 py-1 `}
                              >
                                {renderTimes(day, supermarketSpecialTimes)}
                              </p>
                            )
                          })}{' '}
                        </>
                      )}

                      {renderSpecialOpeningTimes(supermarketSpecialTimes)}
                    </AccordionItemPanel>
                  </AccordionItem>
                  {linkedLocations?.length ? (
                    <AccordionItem uuid="b">
                      <AccordionItemHeading className="border-0 border-b-2 border-white bg-primary-lighter">
                        <AccordionItemButton className="accordion__button | relative p-4 pr-8 text-lg font-medium outline-none flex justify-between items-center">
                          Petrol Station
                        </AccordionItemButton>
                      </AccordionItemHeading>
                      <AccordionItemPanel className="py-2 text-base">
                        {hoursOverride.includes(name) ? (
                          <span className="block w-full text-lg font-bold text-center text-secondary">
                            {/* New Store Opening 12/12/2024 */}
                          </span>
                        ) : (
                          <>
                            {pfsOpeningTimes?.openingTimes?.map(day => {
                              return (
                                <p
                                  key={day.day_id}
                                  className={`${
                                    dayId === day.day_id ? 'font-bold' : ''
                                  } flex justify-between px-4  md:px-2 xl:px-4 py-1 `}
                                >
                                  {renderTimes(day, pfsSpecialTimes)}
                                </p>
                              )
                            })}{' '}
                          </>
                        )}

                        {renderSpecialOpeningTimes(pfsSpecialTimes)}
                      </AccordionItemPanel>
                    </AccordionItem>
                  ) : null}

                  {!departments?.length
                    ? null
                    : departments
                        .filter(department =>
                          hasValidOpeningTimes(department.openingTimes)
                        )
                        .map((department, i) => {
                          const specialTimes = specialOpeningTimes.find(
                            times => times.name === department.name
                          )
                          return (
                            <AccordionItem key={department.name} uuid={i}>
                              <AccordionItemHeading className="border-0 border-b-2 border-white bg-primary-lighter">
                                <AccordionItemButton className="accordion__button | relative p-4 pr-8 text-lg font-medium outline-none flex justify-between items-center">
                                  {department.serviceName}
                                </AccordionItemButton>
                              </AccordionItemHeading>

                              <AccordionItemPanel className="py-2 text-base">
                                {department.serviceName === 'Pharmacy' ? (
                                  <div className="flex justify-between px-4 pb-4 bg-white">
                                    <span className="font-semibold leading-tight text-center">
                                      <span className="text-secondary">
                                        Please note:
                                      </span>{' '}
                                      This Pharmacy closes for lunchtime each
                                      day. Please check with the store for exact
                                      times.
                                    </span>
                                  </div>
                                ) : null}
                                {hoursOverride.includes(name) ? (
                                  <span className="block w-full text-lg font-bold text-center text-secondary">
                                    {/* New Store Opening 12/12/2024 */}
                                  </span>
                                ) : (
                                  <>
                                    {department?.openingTimes?.map(day => {
                                      return (
                                        <p
                                          key={day.day_id}
                                          className={`${
                                            dayId === day.day_id
                                              ? 'font-bold'
                                              : ''
                                          } flex justify-between px-4 py-1 `}
                                        >
                                          {renderTimes(day, specialTimes)}
                                        </p>
                                      )
                                    })}{' '}
                                  </>
                                )}
                                {renderSpecialOpeningTimes(specialTimes)}
                                {department.serviceName === 'Cafe' ? (
                                  <div className="flex flex-col justify-between px-4 py-2 bg-white">
                                    <span className="font-semibold leading-tight text-center">
                                      <span className="text-secondary">
                                        Please note:
                                      </span>{' '}
                                      Our café serves hot food until 15 minutes*
                                      prior to the café closing hours, we will
                                      continue to serve our selection of cakes,
                                      sandwiches & drinks until our closing
                                      time.
                                    </span>
                                    <span className="pt-2 text-sm font-medium leading-tight text-center">
                                      *Sunday's stop serving hot food 30 minutes
                                      prior to closing.
                                    </span>
                                  </div>
                                ) : null}
                              </AccordionItemPanel>
                            </AccordionItem>
                          )
                        })}
                </Accordion>
                <InfoBannerSide store={name} />
                <section
                  itemType="https://schema.org/LocalBusiness"
                  className={`p-4 mb-4 rounded-lg  bg-gradient-br-primary
                  `}
                >
                  <section
                    itemType="schema.org/PostalAddress"
                    className="font-semibold text-white"
                  >
                    <div className="mb-4">
                      <p itemProp="streetAddress">{address?.addressLine1}</p>
                      {address?.addressLine2 &&
                      address?.addressLine2 !== 'None' ? (
                        <p>{address.addressLine2}</p>
                      ) : null}
                      <p>
                        <span itemProp="addressLocality">{address?.city}</span>{' '}
                        <span itemProp="postalCode">{address?.postcode}</span>
                      </p>
                    </div>
                  </section>
                  <a
                    rel="noopener noreferrer"
                    target="_blank"
                    href={`https://www.google.com/maps/dir//Morrisons+${formatAddress`${
                      address?.addressLine1
                    }
                  ${
                    address?.addressLine2 === 'None'
                      ? ''
                      : address?.addressLine2
                  }
                  ${address?.city}
                  ${address?.postcode}`}/@${location?.latitude},${
                      location?.longitude
                    }/`}
                    onClick={clickGetDirections}
                    className={`px-4 py-3 w-full flex justify-between items-center text-center rounded-lg  transition-fast bg-white  hover:bg-primary-lighter text-secondary hover:text-primary`}
                  >
                    <span className="font-semibold whitespace-nowrap">
                      Get directions
                    </span>

                    <Icon icon="locationpin" className="fill-current" />
                  </a>
                </section>
                {parkingItem ? (
                  <section className="p-4 mb-4 rounded-lg bg-gradient-br-parking">
                    <div className="flex items-center justify-start">
                      <div className="flex items-center justify-center p-2 mr-4 font-bold bg-white">
                        <Icon icon="parkingicon" />
                      </div>
                      <div className="flex flex-wrap items-center justify-start flex-1">
                        <span className="w-full text-base font-semibold text-white">
                          Maximum Parking Time
                        </span>
                        <span className="text-xl font-bold text-white">
                          {formatTime(parseInt(parkingItem.maxParkingTime))}
                        </span>
                      </div>
                    </div>
                  </section>
                ) : null}
                {!liveLink ? null : (
                  <section className="p-4 mb-4 bg-primary-lighter">
                    <div className="flex items-end justify-start mb-2 font-semibold text-secondary">
                      <Icon
                        icon="googlelive"
                        className="w-6 mr-1 fill-current"
                      />
                      Live:&nbsp;
                      <span className="text-body whitespace-nowrap">
                        How busy is this store?
                      </span>
                    </div>
                    <div className="mb-4 text-base">
                      Use Google Maps to check popular times at this store.
                      Scroll down to the &lsquo;Popular times&apos; graph.
                    </div>
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={liveLink}
                      onClick={clickCheckIfBusy}
                      className={`text-white px-4 py-3 w-full flex justify-between items-center text-center rounded-lg  transition-fast bg-secondary hover:text-secondary hover:bg-white`}
                    >
                      <span className="font-semibold whitespace-nowrap">
                        Check Now
                      </span>
                      <Icon icon="opennewwindow" className="fill-current" />
                    </a>
                  </section>
                )}
                <HelpDetails
                  category={`${category}`}
                  telephone={`${telephone}`}
                  pharmacy={pharmacy.ExtensionValue}
                />
              </section>
              <section className="w-full md:w-3/5 lg:w-3/4 md:ml-6 lg:ml-8">
                <StoreHighlights highlights={serviceHighlights} />
                <InStoreServices services={services} />
                {category !== 'McColls' ? (
                  <section className="flex flex-wrap w-full md:flex-wrap lg:flex-nowrap">
                    <div className="flex flex-auto w-full mb-4 mr-0 lg:mb-0 sm:w-full lg:w-auto lg:flex-1 lg:mr-2">
                      <MarketingOne services={services} />
                    </div>
                    <div className="flex-auto w-full mb-0 ml-0 sm:mb-4 lg:mb-0 sm:w-full lg:w-auto lg:flex-1 lg:ml-2">
                      <MarketingTwo />
                    </div>
                  </section>
                ) : null}

                <NearbyStores
                  stores={nearbyStores}
                  isMorrisonsDaily={category === 'McColls'}
                />
              </section>
            </section>
          )}
        </main>
      </Container>
    </>
  )
}

StorePage.propTypes = propTypes

export default StorePage

export const Head = ({ pageContext }) => {
  const { storeName } = pageContext
  const { category } = pageContext
  return <SEO title={`${category} ${storeName} Store Details`} />
}
